import type { Container, Point } from 'pixi.js';
import { isMobile } from 'pixi.js';
import cloneDeep from 'lodash/cloneDeep';
import { BoardModel } from '../models/board/BoardModel';
import { GAME_OBJECT_POOL } from '../../GameConstants';
import type { TileStackModel } from '../models/board/TileStackModel';
import MathUtils from '../../../utils/MathUtils';
import { TILE_HEIGHT } from '../views/board/TileView';
import { DragController } from '../../utils/DragController';
import {
  MOBILE_DRAG_OFFSET_MAX_HEIGHT_RATIO,
  MOBILE_DRAG_OFFSET_Y,
  TRAY_SETTINGS,
} from '../../scene/GameSceneConstants';
import { TrayModel } from '../models/tray/TrayModel';
import { GameAreaView } from '../GameAreaView';
import type { TrayStackView } from '../views/tray/TrayStackView';
import type { TileStackView } from '../views/board/TileStackView';
import { BoardHintsController } from './BoardHintsController';
import type { GameTutorialController } from '../../tutorials/game/GameTutorialController';
import type { LevelData } from '../../level/LevelData';
import { BoardViewScheduler } from '../models/board/ViewScheduler';
import { extractLevelRefillColors } from '../../level/utils/extractLevelRefillColors';
import type { BoardView } from '../views/board/BoardView';
import type { TrayView } from '../views/tray/TrayView';
import type { BoardHintsView } from '../views/board/BoardHintsView';
import { AdBlockerController } from './blockers/AdBlockerController';
import { CoinBlockerController } from './blockers/CoinBlockerController';
import type { App } from '../../../app/App';

export interface GameAreaControllerSettings {
  app?: App;
  levelData: LevelData;
  dragContainer: Container;
  rewardedAdTrigger: (actionId: string) => Promise<boolean>;
  isInBoosterMode: () => boolean;
}

export class GameAreaController {
  public readonly dragController: DragController;

  public readonly boardModel: BoardModel;

  public readonly trayModel: TrayModel;

  public readonly view: GameAreaView;

  private readonly hintController: BoardHintsController;

  public tutorialController?: GameTutorialController;

  private windowFocusCallback?: (focus: boolean) => void;

  constructor(private readonly settings: GameAreaControllerSettings) {
    const { levelData } = this.settings;
    const boardScheduler = new BoardViewScheduler();

    this.boardModel = new BoardModel({
      state: levelData.board,
      scheduler: boardScheduler,
    });

    this.trayModel = new TrayModel(
      {
        boardModel: this.boardModel,
        ...TRAY_SETTINGS,
        refills: cloneDeep(levelData.refills),
        refillColors: extractLevelRefillColors(levelData),
      },
      levelData.tray,
    );

    this.view = new GameAreaView({
      boardModel: this.boardModel,
      trayModel: this.trayModel,
      trayViewSettings: { ...TRAY_SETTINGS },
    });

    this.view.boardView.onAnimationFinished.connect((id) => {
      boardScheduler.finish(id);
    });

    this.dragController = this.initDragController();
    this.hintController = this.initHintController();

    this.initTray();

    void new AdBlockerController({
      model: this.boardModel,
      view: this.getBoardView(),
      triggerAd: settings.rewardedAdTrigger,
      isInBoosterMode: settings.isInBoosterMode,
    });

    if (settings.app) {
      settings.app.gameEvents.setupInGameHandlers(this.boardModel, this.getBoardView());

      this.windowFocusCallback = (_focus: boolean): void => {
          this.onWindowFocusChange();
      };
      settings.app.platform.addWindowFocusChangeCallback(this.windowFocusCallback);

      void new CoinBlockerController({
        model: this.boardModel,
        view: this.getBoardView(),
        app: settings.app,
        isInBoosterMode: settings.isInBoosterMode,
      });
    }
  }

  public update(deltaTime: number): void {
    this.dragController.update(deltaTime);
    this.boardModel.update(deltaTime);
    this.view.update(deltaTime);
    this.hintController.update(deltaTime);
    this.tutorialController?.update(deltaTime);
  }

  private initDragController(): DragController {
    const dragController = new DragController(this.settings.dragContainer);

    const {
      boardView: {
        pixi: { renderer },
      },
    } = this.view;

    dragController.useClickOffset = !(isMobile.phone || isMobile.tablet);
    dragController.onDragStarted.connect(() => {
      if (!isMobile.phone && !isMobile.tablet) return;
      const yOffset = Math.min(MOBILE_DRAG_OFFSET_Y, renderer.screen.height * MOBILE_DRAG_OFFSET_MAX_HEIGHT_RATIO);
      dragController.setDragOffset(0, yOffset);
    });
    return dragController;
  }

  public setTutorialController(tutorialController: GameTutorialController): void {
    this.tutorialController = tutorialController;
  }

  private initHintController(): BoardHintsController {
    const { view, boardModel } = this;

    const hintController = new BoardHintsController(
      view,
      boardModel,
      (enabled: boolean): void => {
        if (enabled) {
          view.boardView.highlightLayer.unlockSelection('hintController');
        } else {
          view.boardView.highlightLayer.lockSelection('hintController');
        }
      },
      {
        delay: 8500,
        minStacks: 3,
      },
    );

    hintController.scheduleHintCheck(0);

    boardModel.onStateUpdate.connect(() => {
      hintController.scheduleHintCheck();
    });

    return hintController;
  }

  private initTray(): void {
    const { trayView, boardView } = this.view;
    const { hintController } = this;

    trayView.onItemSelected.connect((stackView) => {
      const { tutorialController: tutorial } = this;
      if (tutorial && !tutorial?.canRemoveFromTray(stackView.model)) return;

      trayView.animatePickingStackUp(stackView, boardView.scaleX);

      this.dragController.startDrag({
        container: stackView,
        onRelease: () => {
          const globalPos = stackView.toGlobal(GAME_OBJECT_POOL.getPoint());
          const targetStack = this.getStackToPutTileDown(globalPos);
          GAME_OBJECT_POOL.addPoint(globalPos);
          const targetStackView = targetStack ? boardView.getStackView(targetStack.id) : null;

          if (
            targetStack &&
            !targetStack.locked &&
            targetStackView &&
            (!tutorial || tutorial.canPlaceStack(targetStack))
          ) {
            hintController.clearHints();
            void this.putTileFromTray(stackView, targetStackView);
            // if (tutorial?.checkStepComplete()) tutorial.setNextStep();
          } else {
            trayView.animateReturnToTray(stackView);
          }

          boardView.setSelectionHighlight(null);
        },
        onUpdate: () => {
          const globalPos = stackView.toGlobal(GAME_OBJECT_POOL.getPoint());
          const pointedStack = this.getStackToPutTileDown(globalPos);
          trayView.updateItemHighlight(stackView, pointedStack === null);
          boardView.setSelectionHighlight(pointedStack?.id ?? null);
          GAME_OBJECT_POOL.addPoint(globalPos);
        },
      });
    });
  }

  public async putTileFromTray(trayStackView: TrayStackView, targetView: TileStackView): Promise<void> {
    if (this.boardModel.isIdle()) {
      this.boardModel.beforeMakeMoveClean.emit();
    }

    const { trayView } = this.view;

    await trayView.animateTileToBoard(trayStackView, targetView);
    this.getBoardView().highlightLayer.setSelectionHighlight(null, true);

    this.boardModel.makeMove(targetView.model.id, trayStackView.model.removeAllTiles());

    const shouldRefillTray = !this.trayModel.hasActiveTiles() && !this.boardModel.isBoardFull();
    if (shouldRefillTray) {
      this.trayModel.refillItems();
    }
  }

  public getStackToPutTileDown(globalPos: Point): TileStackModel | null {
    const { boardView } = this.view;

    const stack = boardView.pointerToStack(globalPos);
    if (stack === null) return null;
    if (stack.isEmpty()) return stack;

    return this.findClosestSelectableStack(stack.id, globalPos, TILE_HEIGHT);
  }

  private findClosestSelectableStack(
    originStackId: string,
    globalPointerPos: Point,
    maxRadius = Infinity,
  ): TileStackModel | null {
    const { boardView } = this.view;

    const originView = boardView.getStackView(originStackId);
    if (!originView) return null;
    if (!originView.parent) return null;

    const connectedStacks = originView.model.getConnectedStacks();

    const p1 = originView.parent.toLocal(globalPointerPos, undefined, GAME_OBJECT_POOL.getPoint());

    let minDistance = Infinity;
    let closestStack: TileStackModel | null = null;

    for (let i = 0; i < connectedStacks.length; i += 1) {
      const stack = connectedStacks[i];
      if (!stack.isEmpty()) continue;

      const view = boardView.getStackView(stack.id);
      if (!view) continue;
      const p2 = view.position;
      const distance = MathUtils.getDistanceBetween(p1, p2);
      if (distance < minDistance && distance < maxRadius) {
        minDistance = distance;
        closestStack = stack;
      }
    }
    GAME_OBJECT_POOL.addPoint(p1);
    return closestStack;
  }

  public destroy(): void {
    this.dragController.dispose();
    this.getBoardView().killEvents();
    this.boardModel.destroy();
    this.tutorialController?.destroy();
    if (this.windowFocusCallback) {
      this.settings.app?.platform.removeWindowFocusChangeCallback(this.windowFocusCallback);
      this.windowFocusCallback = undefined;
    }
  }

  public getBoardView(): BoardView {
    return this.view.boardView;
  }

  public getTrayView(): TrayView {
    return this.view.trayView;
  }

  public getHintsView(): BoardHintsView {
    return this.view.hintsView;
  }

  private onWindowFocusChange(): void {
    this.dragController.windowFocusChange();
  }
}
